import axios from 'axios';
import API_CONFIG from '../config/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_CONFIG.API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Asset API service
export const assetService = {
  // Get all assets with pagination and filtering
  getAssets: async (params = {}) => {
    try {
      const response = await api.get('/assets', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Get single asset by ID
  getAsset: async (id) => {
    try {
      const response = await api.get(`/assets/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Create new asset
  createAsset: async (assetData) => {
    try {
      const response = await api.post('/assets', assetData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Update asset
  updateAsset: async (id, assetData) => {
    try {
      const response = await api.put(`/assets/${id}`, assetData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Delete asset (soft delete)
  deleteAsset: async (id) => {
    try {
      const response = await api.delete(`/assets/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Get asset statistics
  getAssetStats: async () => {
    try {
      const response = await api.get('/assets/stats');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Get assets by user
  getAssetsByUser: async (userId, params = {}) => {
    try {
      const response = await api.get(`/assets/user/${userId}`, { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Add note to asset
  addNote: async (id, note) => {
    try {
      const response = await api.post(`/assets/${id}/notes`, { note });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Upload document for asset
  uploadDocument: async (id, formData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.post(`/assets/${id}/documents`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Get asset documents
  getAssetDocuments: async (id) => {
    try {
      const response = await api.get(`/assets/${id}/documents`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }
};

export default assetService;
